import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Link, Route, Routes, useLocation } from 'react-router';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { AppShell } from './components/AppShell';
import { HomeLayout, ScreenLayout } from './components/layouts';
import { ReadAnnouncementsProvider } from './components/ReadAnnouncementsProvider';
import { SessionProvider } from './components/SessionProvider';
import { EmptyBlock } from './components/states';
import { queryClient } from './lib/queryClient';
import { rememberReturnTo } from './lib/returnTo';
import { useSession } from './lib/session';
import { AccountPage } from './pages/AccountPage';
import { AnnouncementsPage } from './pages/AnnouncementsPage';
import { BadgeScanPage } from './pages/BadgeScanPage';
import { ConsumeSignInPage } from './pages/ConsumeSignInPage';
import { DirectoryPage } from './pages/DirectoryPage';
import { HomePage } from './pages/HomePage';
import { MatchesPage } from './pages/MatchesPage';
import { PersonPage } from './pages/PersonPage';
import { ProgramPage } from './pages/ProgramPage';
import { QuestionnairePage } from './pages/QuestionnairePage';
import { SchedulePage } from './pages/SchedulePage';
import { SessionPage } from './pages/SessionPage';
import { SignInPage } from './pages/SignInPage';
import { SpeakerPage } from './pages/SpeakerPage';
import { SpeakersPage } from './pages/SpeakersPage';
import { UnsubscribePage } from './pages/UnsubscribePage';
import { VenuePage } from './pages/VenuePage';
import { WelcomeVideoPage } from './pages/WelcomeVideoPage';

export function App() {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <ReadAnnouncementsProvider>
            <BrowserRouter>
              <AppShell>
                <AppRoutes />
              </AppShell>
            </BrowserRouter>
          </ReadAnnouncementsProvider>
        </SessionProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

/**
 * Nothing is readable without a session (client decision, Aug 20). The sign-in screen replaces
 * the routes without touching the URL, and signing in then lands on the home screen — a shared
 * link is currently a way into the app, not a way to that screen.
 *
 * Two emailed links are the exception, and have to be. The sign-in link is the one URL that must
 * render before a session exists, because following it is what creates one. The unsubscribe link
 * (MSG-03) is the one URL that must render for somebody who may never sign in again — they are
 * reading the mail on a laptop, and asking them to sign in before they can stop unwanted email
 * would make it the hardest thing in the product to do. Both are matched ahead of the gate.
 *
 * The two layouts are what the design asks for: home wears the lockup and the footer, every
 * other screen wears the tab bar, and sign-in wears neither.
 */
function AppRoutes() {
  const { session, isPending } = useSession();
  const { pathname } = useLocation();

  if (
    pathname.startsWith('/sign-in/') ||
    pathname.startsWith('/unsubscribe/') ||
    pathname === '/welcome'
  ) {
    return (
      <Routes>
        <Route path="/sign-in/:token" element={<ConsumeSignInPage />} />
        <Route path="/unsubscribe/:messageId/:signature" element={<UnsubscribePage />} />
        <Route path="/welcome" element={<WelcomeVideoPage />} />
      </Routes>
    );
  }

  // A held session is answered by the API, not by this browser, so there is a round trip before
  // anything can be shown. Rendering the sign-in screen during it would flash it at somebody who
  // is already signed in, every time they open the app.
  if (isPending) {
    return <SessionPending />;
  }

  if (!session) {
    rememberReturnTo(pathname);
    return <SignInPage />;
  }

  return (
    <Routes>
      <Route element={<HomeLayout />}>
        <Route path="/" element={<HomePage />} />
      </Route>

      {/* The questionnaire wears no tab bar: it is a task with its own way out (PRF-08). */}
      <Route path="/account/questionnaire" element={<QuestionnairePage />} />

      <Route element={<ScreenLayout />}>
        <Route path="/program" element={<ProgramPage />} />
        <Route path="/program/:idOrSlug" element={<SessionPage />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/matches" element={<MatchesPage />} />
        <Route path="/announcements" element={<AnnouncementsPage />} />
        <Route path="/directory" element={<DirectoryPage />} />
        <Route path="/directory/:id" element={<PersonPage />} />
        <Route path="/b/:token" element={<BadgeScanPage />} />
        <Route path="/speakers" element={<SpeakersPage />} />
        <Route path="/speakers/:id" element={<SpeakerPage />} />
        <Route path="/venue" element={<VenuePage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route
          path="*"
          element={
            <EmptyBlock title="That screen does not exist">
              <Link to="/" className="font-semibold text-brand-text underline">
                Back to the home screen
              </Link>
            </EmptyBlock>
          }
        />
      </Route>
    </Routes>
  );
}

/** Deliberately not a spinner: it is one request on a warm cache, and a flash of chrome reads calmer. */
function SessionPending() {
  return (
    <main className="px-gutter flex min-h-dvh items-center justify-center">
      <p className="text-subtitle text-ink-4" role="status">
        Loading…
      </p>
    </main>
  );
}
